import React, { useState } from 'react';
import { styled } from 'styletron-react';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';

// --- Styled ---

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#fff',
  fontFamily:
    "'Red Hat Text', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  color: '#111827',
});

const Content = styled('div', {
  maxWidth: '760px',
  margin: '0 auto',
  padding: '24px 20px 80px',
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '24px',
});

const HeaderLeft = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const Logo = styled('img', {
  height: '40px',
  objectFit: 'contain',
});

const TenantName = styled('div', {
  fontSize: '16px',
  fontWeight: '600',
});

const LangToggle = styled('button', (props: { $active: boolean }) => ({
  border: 'none',
  background: props.$active ? '#111827' : '#F3F4F6',
  color: props.$active ? '#fff' : '#111827',
  padding: '6px 12px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
}));

const Title = styled('h1', {
  fontSize: '32px',
  fontWeight: '700',
  margin: '8px 0 4px',
  lineHeight: '1.2',
});

const Subtitle = styled('p', {
  color: '#6B7280',
  fontSize: '14px',
  margin: '0 0 28px',
});

const SectionTitle = styled('h2', {
  fontSize: '20px',
  fontWeight: '700',
  margin: '28px 0 12px',
});

const Paragraph = styled('p', {
  fontSize: '15px',
  lineHeight: '1.6',
  margin: '0 0 12px',
});

const List = styled('ul', {
  paddingLeft: '20px',
  margin: '0 0 16px',
});

const Item = styled('li', {
  fontSize: '15px',
  lineHeight: '1.7',
  marginBottom: '6px',
});

const ContactBlock = styled('div', {
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '16px 20px',
  marginTop: '24px',
});

// --- Content (EN / ES) ---

type Lang = 'en' | 'es';

const TEXT = {
  en: {
    title: 'Privacy Policy',
    effective: 'Effective date',
    intro:
      'This Privacy Policy explains how we collect, use, and protect personal information when you use our mobile applications and related services (the "Services"). By using the Services you agree to the practices described in this policy.',
    sections: {
      info: 'Information We Collect',
      use: 'How We Use Information',
      share: 'How We Share Information',
      security: 'Data Security',
      rights: 'Your Rights & Choices',
      children: "Children's Privacy",
      changes: 'Changes to This Policy',
      contact: 'Contact Us',
    },
    info: [
      'Identity information you provide: name, last name, nationality, government-issued ID document and document photo (for access verification of guests and residents).',
      'Contact information: email address and phone number.',
      'Profile photo (optional).',
      'Property information: residence/unit you are associated with and your role (owner, resident, guest, etc.).',
      'Device information: device identifier, operating system version, and push-notification token, used to send access notifications.',
      'Usage information: actions you take in the app such as creating invitations, bookings, and payments.',
      'Payment information: card brand and last four digits via our PCI-compliant payment processor. Full card numbers are never stored on our servers.',
    ],
    use: [
      'Provide and operate the Services (gate access, guest invitations, bookings, payments).',
      'Verify the identity of residents and guests at access points.',
      'Send transactional notifications (invitation accepted, booking confirmation, payment receipt).',
      'Improve, debug, and secure the Services (error monitoring, fraud prevention).',
      'Comply with legal obligations.',
    ],
    share: [
      'Service providers acting on our behalf: cloud hosting (AWS), authentication (AWS Cognito), payments (CyberSource), push notifications (Firebase Cloud Messaging), error monitoring (Sentry).',
      'Property administrators of the residential community where you are a resident or guest, for access management and operations.',
      'Law enforcement or government authorities when required by law.',
      'We do not sell personal information.',
    ],
    security:
      'We use industry-standard safeguards including encryption in transit (HTTPS/TLS), encryption at rest, signed authentication tokens, and access controls. No method of transmission or storage is 100% secure; we cannot guarantee absolute security.',
    rights: [
      'Access — request a copy of the data we hold about you.',
      'Correction — ask us to update inaccurate data.',
      'Deletion — request that we delete your account and personal data (available in-app under Profile → Delete User, or by contacting us).',
      'Withdraw consent — disable optional features (biometrics, photo upload) at any time.',
    ],
    children:
      'The Services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, contact us and we will delete it.',
    changes:
      'We may update this Privacy Policy from time to time. Material changes will be notified in-app or via email. The "Effective date" above reflects the latest update.',
    contactBody:
      'For privacy questions, data deletion requests, or any other matter related to this policy, contact us at:',
  },
  es: {
    title: 'Política de Privacidad',
    effective: 'Fecha de vigencia',
    intro:
      'Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos la información personal cuando usas nuestras aplicaciones móviles y servicios relacionados (los "Servicios"). Al usar los Servicios aceptas las prácticas descritas en esta política.',
    sections: {
      info: 'Información que Recopilamos',
      use: 'Cómo Usamos la Información',
      share: 'Cómo Compartimos la Información',
      security: 'Seguridad de los Datos',
      rights: 'Tus Derechos y Opciones',
      children: 'Privacidad de Menores',
      changes: 'Cambios a esta Política',
      contact: 'Contáctanos',
    },
    info: [
      'Información de identidad: nombre, apellido, nacionalidad, documento de identidad oficial y foto del documento (para verificación de acceso de invitados y residentes).',
      'Información de contacto: correo electrónico y teléfono.',
      'Foto de perfil (opcional).',
      'Información de propiedad: residencia/unidad asociada y rol (propietario, residente, invitado, etc.).',
      'Información del dispositivo: identificador, versión del sistema operativo y token de notificaciones push, usados para enviar avisos de acceso.',
      'Información de uso: acciones realizadas en la app como crear invitaciones, reservaciones y pagos.',
      'Información de pago: marca de tarjeta y últimos cuatro dígitos vía nuestro procesador de pagos PCI-compliant. Los números completos de tarjeta nunca se almacenan en nuestros servidores.',
    ],
    use: [
      'Proveer y operar los Servicios (acceso a portería, invitaciones, reservaciones, pagos).',
      'Verificar la identidad de residentes e invitados en los puntos de acceso.',
      'Enviar notificaciones transaccionales (invitación aceptada, confirmación de reserva, recibo de pago).',
      'Mejorar, depurar y asegurar los Servicios (monitoreo de errores, prevención de fraude).',
      'Cumplir obligaciones legales.',
    ],
    share: [
      'Proveedores de servicio actuando en nuestro nombre: hosting en la nube (AWS), autenticación (AWS Cognito), pagos (CyberSource), notificaciones push (Firebase Cloud Messaging), monitoreo de errores (Sentry).',
      'Administradores de la propiedad o comunidad residencial donde eres residente o invitado, para gestión de acceso y operaciones.',
      'Autoridades cuando sea requerido por ley.',
      'No vendemos información personal.',
    ],
    security:
      'Usamos protecciones estándar de la industria: cifrado en tránsito (HTTPS/TLS), cifrado en reposo, tokens de autenticación firmados, y control de accesos. Ningún método de transmisión o almacenamiento es 100% seguro; no podemos garantizar seguridad absoluta.',
    rights: [
      'Acceso — solicitar copia de los datos que tenemos sobre ti.',
      'Corrección — pedir actualización de datos incorrectos.',
      'Eliminación — solicitar la eliminación de tu cuenta y datos personales (disponible dentro de la app en Perfil → Eliminar Usuario, o contactándonos).',
      'Retirar consentimiento — desactivar funciones opcionales (biometría, subida de foto) en cualquier momento.',
    ],
    children:
      'Los Servicios no están dirigidos a menores de 13 años. No recopilamos conscientemente información personal de menores de 13. Si crees que un menor nos ha proporcionado información personal, contáctanos y la eliminaremos.',
    changes:
      'Podemos actualizar esta Política de Privacidad periódicamente. Cambios materiales se notificarán dentro de la app o por correo. La "Fecha de vigencia" arriba refleja la última actualización.',
    contactBody:
      'Para consultas de privacidad, solicitudes de eliminación de datos, o cualquier otro tema relacionado con esta política, contáctanos en:',
  },
} as const;

const EFFECTIVE_DATE = '2026-05-14';

export const PrivacyPolicy: React.FC = () => {
  usePublicPageScroll();
  const initial: Lang =
    (typeof navigator !== 'undefined' &&
      navigator.language?.toLowerCase().startsWith('es'))
      ? 'es'
      : 'en';
  const [lang, setLang] = useState<Lang>(initial);
  const t = TEXT[lang];

  const { tenant } = useTenantBranding();
  const tenantName = tenant?.name || 'City Place';
  const { imageUrl: logoUrl } = useS3Image(tenant?.settings?.logo);
  const supportEmail =
    (tenant?.settings as any)?.supportEmail || 'info@xhinola.io';

  return (
    <Page>
      <Content>
        <Header>
          <HeaderLeft>
            {logoUrl ? (
              <Logo src={logoUrl} alt={tenantName} />
            ) : null}
            <TenantName>{tenantName}</TenantName>
          </HeaderLeft>
          <div style={{ display: 'flex', gap: '6px' }}>
            <LangToggle $active={lang === 'en'} onClick={() => setLang('en')}>
              EN
            </LangToggle>
            <LangToggle $active={lang === 'es'} onClick={() => setLang('es')}>
              ES
            </LangToggle>
          </div>
        </Header>

        <Title>{t.title}</Title>
        <Subtitle>
          {t.effective}: {EFFECTIVE_DATE}
        </Subtitle>

        <Paragraph>{t.intro}</Paragraph>

        <SectionTitle>{t.sections.info}</SectionTitle>
        <List>
          {t.info.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.use}</SectionTitle>
        <List>
          {t.use.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.share}</SectionTitle>
        <List>
          {t.share.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.security}</SectionTitle>
        <Paragraph>{t.security}</Paragraph>

        <SectionTitle>{t.sections.rights}</SectionTitle>
        <List>
          {t.rights.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.children}</SectionTitle>
        <Paragraph>{t.children}</Paragraph>

        <SectionTitle>{t.sections.changes}</SectionTitle>
        <Paragraph>{t.changes}</Paragraph>

        <SectionTitle>{t.sections.contact}</SectionTitle>
        <Paragraph>{t.contactBody}</Paragraph>
        <ContactBlock>
          <div style={{ fontWeight: 600 }}>{tenantName}</div>
          <div>
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </div>
        </ContactBlock>
      </Content>
    </Page>
  );
};
